import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Users, Crown, Globe, Monitor, MonitorSmartphone, Shield, CheckCircle, Star, TrendingUp, Zap, Clock, Heart, Award } from 'lucide-react';
import { UsersIcon, UserCountDisplay } from './icons/UsersIcon';
import OptimizedProductCard from './OptimizedProductCard';

const ProductsPage = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const [selectedDuration, setSelectedDuration] = useState(1); // 默认选择1个月

 // 产品配置数据
 const productCategories = [
 {
 id: 'official-exclusive',
 name: '官网独享账号',
 description: '专属个人使用，完全隐私保护',
 icon: <Crown className="w-8 h-8" />,
 gradient: 'from-accent-500 to-accent-600',
 bgGradient: 'from-accent-50 via-cream-50 to-accent-50',
 borderColor: 'border-accent-200',
 users: 1,
 features: ['🔒 个人专属', '🛡️ 永久质保', '🚀 即开即用'],
 pricing: {
 1: { price: 4.9, originalPrice: 20, productId: 228 },
 3: { price: 13.9, originalPrice: 60, productId: 228 },
 12: { price: 49.9, originalPrice: 240, productId: 228 }
 },
 highlights: ['隐私安全', '永久质保', '完整功能']
 },
 {
 id: 'official-renewal', 
 name: '官网账号续费',
 description: '老用户续费专享优惠价格',
 icon: <Shield className="w-8 h-8" />,
 gradient: 'from-green-500 to-green-600',
 bgGradient: 'from-green-50 via-cream-50 to-green-100',
 borderColor: 'border-green-200',
 users: 1,
 features: ['💰 续费优惠', '🔄 无缝续费', '👑 VIP服务'],
 pricing: {
 1: { price: 3.9, originalPrice: 20, productId: 229 },
 3: { price: 10.9, originalPrice: 60, productId: 229 },
 12: { price: 39.9, originalPrice: 240, productId: 229 }
 },
 highlights: ['续费专享', '价格优惠', '老用户福利']
 },
 {
 id: 'official-shared',
 name: '官网3人共享账号', 
 description: '3人团队共享，性价比之选',
 icon: <Users className="w-8 h-8" />,
 gradient: 'from-accent-500 to-accent-600',
 bgGradient: 'from-accent-50 via-cream-50 to-accent-100',
 borderColor: 'border-accent-200',
 users: 3,
 features: ['👥 3人共享', '💎 团队协作', '📈 性价比高'],
 pricing: {
 1: { price: 7.9, originalPrice: 20, productId: 230 },
 3: { price: 21.9, originalPrice: 60, productId: 230 },
 12: { price: 79.9, originalPrice: 240, productId: 230 }
 },
 highlights: ['团队共享', '成本分摊', '官方网站']
 },
 {
 id: 'web-shared-hosting',
 name: '网页共享托管账号',
 description: '便捷托管服务，免登录使用',
 icon: <Globe className="w-8 h-8" />,
 gradient: 'from-accent-500 to-accent-600',
 bgGradient: 'from-accent-50 via-cream-50 to-accent-100',
 borderColor: 'border-accent-200',
 users: 1,
 features: ['🌐 免登录', '⚡ 即用即开', '🔧 托管服务'],
 pricing: {
 1: { price: 3.9, originalPrice: 20, productId: 301 },
 3: { price: 10.9, originalPrice: 60, productId: 301 },
 12: { price: 39.9, originalPrice: 240, productId: 301 }
 },
 highlights: ['免登录', '便捷使用', '托管服务']
 }
 ];

 const durations = [
 { value: 1, label: '1个月', popular: false },
 { value: 3, label: '3个月', popular: true },
 { value: 12, label: '12个月', popular: false }
 ];

 const handleProductClick = (product) => {
 const pricing = product.pricing[selectedDuration];
 navigate(`/product/${pricing.productId}?type=gpt`);
 };

 const calculateMonthlyPrice = (totalPrice, months) => {
 return (totalPrice / months).toFixed(2);
 };

 const calculateSavings = (price, originalPrice) => {
 return (originalPrice - price).toFixed(2);
 };

 return (
 <div className="route-surface catalog-page min-h-screen bg-cream-100">
 {/* Hero Section */}
 <section className="py-16 lg:py-24 bg-gradient-to-br from-accent-50 via-cream-50 to-accent-100">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-12">
 <div className="inline-flex items-center bg-accent-100 text-accent-600 rounded-full px-6 py-3 text-sm font-semibold mb-6">
 <ShoppingCart className="w-4 h-4 mr-2" />
 产品中心
 </div>
 <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
 选择适合您的
 <span className="bg-gradient-to-r from-accent-500 to-accent-600 bg-clip-text text-transparent"> AI 产品</span>
 </h1>
 <p className="text-xl text-ink-600 max-w-3xl mx-auto leading-relaxed mb-8">
 我们提供多种产品选择，满足个人用户到团队协作的不同需求
 </p>
 
 {/* 时长选择器 */}
 <div className="inline-flex items-center bg-cream-50 rounded-2xl p-2 shadow-lg border border-accent-200">
 {durations.map((duration) => (
 <button
 key={duration.value}
 onClick={() => setSelectedDuration(duration.value)}
 className={`relative px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
 selectedDuration === duration.value
 ? 'bg-accent-500 hover:bg-accent-600 text-white shadow-lg'
 : 'text-ink-600 hover:text-accent-500'
 }`}
 >
 {duration.label}
 {duration.popular && (
 <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
 推荐
 </div>
 )}
 </button>
 ))}
 </div>
 </div>
 </div>
 </section>

 {/* Products Grid */}
 <section className="py-16 lg:py-24">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
 {productCategories.map((product, index) => (
 <OptimizedProductCard
 key={product.id}
 product={product}
 selectedDuration={selectedDuration}
 onProductClick={handleProductClick}
 />
 ))}
 </div>
 </div>
 </section>

 {/* 优势对比区域 */}
 <section className="py-16 lg:py-24 bg-gradient-to-br from-ink-900 to-ink-950">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-12">
 <div className="inline-flex items-center bg-accent-500/20 text-accent-400 rounded-full px-6 py-3 text-sm font-semibold mb-6 backdrop-blur-sm">
 <Award className="w-4 h-4 mr-2" />
 产品优势
 </div>
 <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
 为什么选择我们？
 </h2>
 <p className="text-xl text-ink-300 max-w-3xl mx-auto leading-relaxed">
 专业的AI服务，卓越的用户体验，值得信赖的品质保证
 </p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
 {[
 { icon: <Zap className="w-8 h-8" />, title: '极速开通', desc: '30秒内完成账号开通，立即开始使用' },
 { icon: <Shield className="w-8 h-8" />, title: '安全可靠', desc: '企业级安全保障，数据隐私完全保护' },
 { icon: <Clock className="w-8 h-8" />, title: '永久质保', desc: '提供永久质保服务，无后顾之忧' },
 { icon: <Heart className="w-8 h-8" />, title: '贴心服务', desc: '7×24小时客服支持，随时解答疑问' }
 ].map((item, index) => (
 <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition-all duration-300">
 <div className="bg-accent-500 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 text-white">
 {item.icon}
 </div>
 <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
 <p className="text-ink-300 leading-relaxed">{item.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* 安全保障区域 */}
 <section className="py-12 bg-gradient-to-r from-accent-50 to-accent-100">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="flex items-center justify-center gap-8 text-sm text-ink-600 flex-wrap">
 <div className="flex items-center gap-2">
 <Shield className="w-5 h-5 text-green-500" />
 <span className="font-medium">SSL安全加密</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-2">
 <CheckCircle className="w-5 h-5 text-accent-500" />
 <span className="font-medium">7天无理由退款</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-2">
 <Clock className="w-5 h-5 text-accent-500" />
 <span className="font-medium">24小时客服支持</span>
 </div>
 <div className="w-1 h-1 bg-ink-300 rounded-full"></div>
 <div className="flex items-center gap-2">
 <Award className="w-5 h-5 text-accent-500" />
 <span className="font-medium">品质保证</span>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
};

export default ProductsPage;